import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Printer,
  Receipt,
  Loader2,
  TicketCheck,
  ScanLine,
  Search,
  AlertTriangle,
  RotateCw,
  Phone,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { EmptyState } from '@/components/pos/EmptyState';
import { ReceiptPreview } from '@/components/pos/ReceiptPreview';
import { ReprintReasonDialog } from '@/components/pos/ReprintReasonDialog';
import { GroupClaimPanel } from '@/components/pos/GroupClaimPanel';
import { activeTickets, claimButtonLabel, claimOf, isPartnerBooking } from '@/lib/groupClaim';
import {
  useSaleDetail,
  useLatestOrderByPhone,
  usePrintBatch,
  usePrintSingle,
  useReprintBatch,
  useReprintWithNewQr,
} from '@/hooks/mutations';
import type { ReprintInput } from '@/api/qrCode.api';
import type { SaleTicket } from '@/types/sale';
import { ticketLabel } from '@/lib/ticketLabel';
import { useDeviceStore } from '@/stores/device.store';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useHotkeys } from '@/hooks/useHotkeys';
import { printReceipt } from '@/lib/printReceipt';
import { printRefusalMessage, summarizePrintBatch } from '@/lib/printRefusalErrors';
import type { SaleDetail } from '@/types/sale';
import type { LatestOrderByPhone } from '@/api/sale.api';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';

/** What the lookup box takes: an order number, or the buyer's phone number. */
type LookupMode = 'invoice' | 'phone';

/** An order number, typed or scanned — always looked up as one. */
const ORDER_CODE = /^(INV|GRP)-/i;

/**
 * /print-ticket — dedicated "print an existing booking" flow.
 *
 * The fast path for a customer who bought online (or lost their printout):
 * type or SCAN the booking number, and print its tickets. A wristband prints
 * ONCE; one already printed (here, at a kiosk or in the back office) can only
 * be reprinted from the ticketbox with a reason, and gets a NEW QR — the old
 * wristband and the e-ticket stop working. Distinct from
 * /orders (which is a browse/search surface) — this is a single-shot lookup
 * tuned for a keyboard-wedge scanner and a big print button.
 *
 * ABT-S22: a travel agent's group is found by its INV-… or GRP-… and CLAIMS
 * its wristbands once — the whole group together (no partial claim) in one
 * press; the server refuses a second claim, a cancelled or unpaid booking and
 * another day's booking. Its panel shows no prices, and there is no receipt.
 *
 * "Nomor HP" mode: the buyer's phone (typed any way: 08…, +62…) opens the
 * phone's latest ACTIVE order (`GET /sale/lookup/by-phone`: paid, visiting
 * today or later, any channel; today's visit first). A scan, or an INV-/GRP-
 * code typed in that mode, is still looked up as an order number.
 */
export function PrintTicketPage() {
  const [params, setParams] = useSearchParams();
  const printerBound = useDeviceStore((s) => Boolean(s.ticketPrinterId));

  const [mode, setMode] = useState<LookupMode>(
    params.get('phone') && !params.get('booking') ? 'phone' : 'invoice',
  );
  const [code, setCode] = useState(
    params.get('booking')?.toUpperCase() ?? params.get('phone') ?? '',
  );
  const [detail, setDetail] = useState<SaleDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  // The order a phone lookup resolved to, shown above the receipt.
  const [byPhone, setByPhone] = useState<(LatestOrderByPhone & { phone: string }) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Only the latest lookup may land: in phone mode a scan types into the box,
  // so its Enter fires a (stale) phone lookup just before the scan's own.
  const lookupSeq = useRef(0);

  const { mutate: fetchDetail, isPending: fetchingDetail } = useSaleDetail();
  const { mutate: findByPhone, isPending: findingPhone } = useLatestOrderByPhone();
  const looking = fetchingDetail || findingPhone;
  const { mutate: printBatch, isPending: printingBatch } = usePrintBatch();
  const { mutate: printOne, isPending: printingOne } = usePrintSingle();
  const { mutate: reprintBatch, isPending: reprintingBatch } = useReprintBatch();
  const { mutate: reprintOne, isPending: reprintingOne } = useReprintWithNewQr();
  const printing = printingBatch || printingOne || reprintingBatch || reprintingOne;
  // What the reason dialog will reprint: every printed ticket, or one.
  const [reprintTarget, setReprintTarget] = useState<
    { kind: 'all'; count: number } | { kind: 'one'; id: string; label: string } | null
  >(null);

  const showDetail = (invoiceId: string, seq: number) => {
    fetchDetail(invoiceId, {
      onSuccess: (d) => {
        if (seq !== lookupSeq.current) return;
        if (!d || !d.invoiceId) {
          setDetail(null);
          setNotFound(true);
          return;
        }
        setDetail(d);
      },
      onError: (err) => {
        if (seq !== lookupSeq.current) return;
        setDetail(null);
        setNotFound(true);
        toast.error(err.message || 'Pesanan tidak ditemukan.');
      },
    });
  };

  const lookup = (raw?: string, as: LookupMode = mode) => {
    const typed = (raw ?? code).trim();
    const asMode: LookupMode = as === 'phone' && ORDER_CODE.test(typed) ? 'invoice' : as;

    if (asMode === 'phone') {
      if (typed.replace(/\D/g, '').length < 8) {
        toast.warning(typed ? 'Nomor HP minimal 8 digit.' : 'Masukkan nomor HP dulu.');
        inputRef.current?.focus();
        return;
      }
      const seq = ++lookupSeq.current;
      if (mode !== 'phone') setMode('phone');
      setCode(typed);
      setParams({ phone: typed }, { replace: true });
      setNotFound(false);
      setByPhone(null);
      findByPhone(typed, {
        onSuccess: (found) => {
          if (seq !== lookupSeq.current) return;
          if (!found) {
            setDetail(null);
            setNotFound(true);
            return;
          }
          setByPhone({ ...found, phone: typed });
          showDetail(found.invoiceId, seq);
        },
        onError: (err) => {
          if (seq !== lookupSeq.current) return;
          setDetail(null);
          setNotFound(true);
          toast.error(err.message || 'Gagal mencari pesanan untuk nomor ini.');
        },
      });
      return;
    }

    const q = typed.toUpperCase();
    if (!q) {
      toast.warning('Masukkan nomor pesanan dulu.');
      inputRef.current?.focus();
      return;
    }
    const seq = ++lookupSeq.current;
    if (mode !== 'invoice') setMode('invoice');
    setCode(q);
    setParams({ booking: q }, { replace: true });
    setNotFound(false);
    setByPhone(null);
    showDetail(q, seq);
  };

  /** Switch the box between order number and phone; starts a fresh search. */
  const switchMode = (next: LookupMode) => {
    if (next === mode) return;
    lookupSeq.current += 1; // drop anything still in flight
    setMode(next);
    setCode('');
    setDetail(null);
    setNotFound(false);
    setByPhone(null);
    setParams({}, { replace: true });
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  // Auto-lookup when arriving with ?booking= (e.g. from Beranda / a scan on
  // another page that routed here) or ?phone=.
  useEffect(() => {
    const booking = params.get('booking');
    const phone = params.get('phone');
    if (booking) lookup(booking, 'invoice');
    else if (phone) lookup(phone, 'phone');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard-wedge scanner: a scan anywhere on the page runs the lookup — as an
  // order number, whichever mode the box is in (a barcode is never a phone).
  // `flushOnIdleMs` auto-searches as soon as the scan burst finishes — no need
  // for the scanner to send a trailing Enter.
  useBarcodeScanner({ onScan: (scanned) => lookup(scanned, 'invoice'), flushOnIdleMs: 120 });

  // A voided wristband (a cancelled member) is neither printed nor counted.
  const tickets = activeTickets(detail?.tickets);
  const unprintedCount = tickets.filter((t) => !(t.printCounts ?? 0)).length;
  const printedCount = tickets.length - unprintedCount;
  const partner = isPartnerBooking(detail);
  const summary = claimOf(detail);
  const claimed = Boolean(summary?.groups.some((g) => g.claim));


  // Re-read the booking so the print / reprint split reflects what just happened.
  const refresh = () => {
    if (detail) fetchDetail(detail.invoiceId, { onSuccess: (d) => d?.invoiceId && setDetail(d) });
  };

  /** Main action: print every never-printed ticket; when none is left, offer a reprint. */
  const handlePrintAll = () => {
    if (!detail) return;
    if (!printerBound) {
      toast.warning('Printer belum diatur — hubungkan printer di menu Perangkat.');
      return;
    }
    if (partner && summary) {
      if (!summary.claimable && (!claimed || unprintedCount > 0)) {
        toast.error(summary.refusal?.message ?? 'Rombongan ini tidak dapat diambil sekarang.');
        return;
      }
    }
    if (unprintedCount === 0 || (partner && summary && !summary.claimable)) {
      setReprintTarget({ kind: 'all', count: printedCount });
      return;
    }
    printBatch(detail.invoiceId, {
      onSuccess: (res) => {
        // A MIXED invoice (today's bands + a refunded one, or next Saturday's)
        // gets one toast PER REASON, not a blanket "N gagal": a refusal is
        // about the ticket, so the cashier needs to read which and why. The
        // printer ratio below counts only what the printer was asked for.
        const s = summarizePrintBatch(res.tickets);
        for (const reason of s.refusalReasons) toast.error(reason, { duration: 12_000 });
        if (s.attempted === 0) {
          if (!s.refused) toast.warning('Tidak ada tiket yang dicetak.');
        } else if (s.printed === s.attempted) {
          toast.success(`${s.printed} tiket dicetak.`);
        } else {
          toast.warning(`${s.printed}/${s.attempted} tiket dicetak. Cek printer.`);
        }
        refresh();
      },
      onError: (err) => toast.error(printRefusalMessage(err) ?? err.message),
    });
  };

  const handlePrintOne = (t: SaleTicket) => {
    const name = ticketLabel(t, 'Tiket');
    if ((t.printCounts ?? 0) > 0) {
      setReprintTarget({ kind: 'one', id: t.id, label: name });
      return;
    }
    printOne(t.id, {
      onSuccess: () => {
        toast.success(`${name} dicetak.`);
        refresh();
      },
      // A refusal (#1746) is not a printer problem and saying so is the whole
      // point: the server's own sentence names an ISO day, this names the day
      // in Bahasa and the next step.
      onError: (err) => {
        const refusal = printRefusalMessage(err);
        if (refusal) toast.error(`${name}: ${refusal}`, { duration: 12_000 });
        else toast.error(err.message);
      },
    });
  };

  const confirmReprint = (input: ReprintInput) => {
    if (!detail || !reprintTarget) return;
    const done = () => {
      setReprintTarget(null);
      refresh();
    };
    if (reprintTarget.kind === 'all') {
      reprintBatch(
        { invoiceId: detail.invoiceId, ...input },
        {
          onSuccess: (res) => {
            const ok = res.tickets.filter((t) => t.status === 'PRINTED').length;
            const failed = res.tickets.filter((t) => t.status === 'FAILED');
            if (failed.length) toast.error(`${ok} dicetak ulang, ${failed.length} gagal. ${failed[0]?.message ?? ''}`);
            else toast.success(`${ok} gelang dicetak ulang dengan QR baru.`);
            done();
          },
          onError: (err) => toast.error(err.message),
        },
      );
      return;
    }
    const { id, label } = reprintTarget;
    reprintOne(
      { qrId: id, ...input },
      {
        onSuccess: () => {
          toast.success(`${label} dicetak ulang dengan QR baru.`);
          done();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  useHotkeys({ 'ctrl+p': handlePrintAll });

  const printDisabled =
    !detail ||
    !printerBound ||
    printing ||
    tickets.length === 0 ||
    ['CANCELLED', 'REFUND'].includes(String(detail.status)) ||
    // A partner group that is not claimable and was never claimed here
    // (cancelled, unpaid, another day): nothing to print or reprint.
    (partner && !!summary && !summary.claimable && !claimed);

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-6 overflow-y-auto px-6 py-8 pos-scroll">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-ink-900">
          <TicketCheck className="h-6 w-6 text-crimson-700" /> Cetak Tiket
        </h1>
        <p className="text-sm text-muted-foreground">
          Masukkan atau pindai nomor pesanan, atau cari dengan nomor HP pembeli, untuk mencetak
          tiketnya.
        </p>
      </div>

      {/* Lookup — scanner-friendly, oversized for touch + quick reads. */}
      <Card>
        <CardContent className="pt-6">
          <div
            role="group"
            aria-label="Cari pesanan dengan"
            className="mb-4 inline-flex rounded-xl border-2 border-cream-200 bg-cream-50 p-1"
          >
            {(
              [
                ['invoice', 'Nomor pesanan', ScanLine],
                ['phone', 'Nomor HP', Phone],
              ] as const
            ).map(([m, label, Icon]) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                data-testid={`lookup-mode-${m}`}
                onClick={() => switchMode(m)}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                  mode === m
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-ink-500 hover:bg-secondary',
                )}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          <label
            htmlFor="print-ticket-lookup"
            className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400"
          >
            {mode === 'phone' ? (
              <>
                <Phone className="h-3.5 w-3.5" /> Nomor HP pembeli
              </>
            ) : (
              <>
                <ScanLine className="h-3.5 w-3.5" /> Nomor pesanan
              </>
            )}
          </label>
          <div className="flex gap-3">
            <input
              id="print-ticket-lookup"
              ref={inputRef}
              data-scanner-target
              autoFocus
              type={mode === 'phone' ? 'tel' : 'text'}
              inputMode={mode === 'phone' ? 'tel' : 'text'}
              autoComplete="off"
              value={code}
              onChange={(e) =>
                setCode(mode === 'phone' ? e.target.value : e.target.value.toUpperCase())
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') lookup();
              }}
              placeholder={mode === 'phone' ? '08… atau +62…' : 'INV-AP-… atau GRP-…'}
              className="tnum h-16 flex-1 rounded-xl border-2 border-cream-200 bg-white px-4 font-display text-2xl font-bold uppercase tracking-wide text-ink-900 outline-none transition-colors placeholder:font-sans placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-ink-400 focus:border-crimson-600"
            />
            <Button
              size="lg"
              className="h-16 gap-2 px-8 text-lg"
              onClick={() => lookup()}
              disabled={looking}
            >
              {looking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
              Cari
            </Button>
          </div>
          {mode === 'phone' &&
            (byPhone && detail?.invoiceId === byPhone.invoiceId ? (
              <p data-testid="by-phone-summary" className="mt-3 text-sm text-ink-500">
                Pesanan terbaru:{' '}
                <span className="tnum font-semibold text-ink-900">{byPhone.invoiceId}</span>
                {byPhone.visitDate && <> · kunjungan {formatDate(byPhone.visitDate)}</>}
                {byPhone.otherCount > 0 && (
                  <span className="text-ink-400">
                    {' '}
                    · {byPhone.otherCount} pesanan aktif lain (lihat menu Pesanan)
                  </span>
                )}
              </p>
            ) : (
              <p className="mt-3 text-xs text-ink-400">
                Membuka pesanan terbaru yang sudah dibayar dengan kunjungan hari ini (atau hari
                berikutnya) untuk nomor ini.
              </p>
            ))}
          {!printerBound && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Printer belum diatur — atur di menu Perangkat sebelum mencetak.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Result */}
      <Card className="flex min-h-0 flex-1 flex-col">
        <CardContent className="flex min-h-0 flex-1 flex-col pt-6">
          {looking ? (
            <div className="flex flex-1 items-center justify-center py-16 text-ink-400">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : notFound ? (
            <EmptyState
              icon={Search}
              title={mode === 'phone' ? 'Tidak ada pesanan aktif' : 'Pesanan tidak ditemukan'}
              description={
                mode === 'phone'
                  ? `Tidak ada pesanan aktif (sudah dibayar, kunjungan hari ini atau sesudahnya) untuk nomor "${code}".`
                  : `Tidak ada pesanan dengan nomor "${code}". Periksa kembali nomornya.`
              }
              className="flex-1 border-0 bg-transparent"
            />
          ) : !detail ? (
            <EmptyState
              icon={mode === 'phone' ? Phone : TicketCheck}
              title="Siap mencetak"
              description={
                mode === 'phone'
                  ? 'Masukkan nomor HP pembeli di atas untuk menampilkan pesanan terbarunya.'
                  : 'Masukkan atau pindai nomor pesanan di atas untuk menampilkan tiketnya.'
              }
              className="flex-1 border-0 bg-transparent"
            />
          ) : (
            <>
              <ScrollArea className="flex-1">
                {partner && summary ? (
                  <GroupClaimPanel summary={summary} />
                ) : (
                  <ReceiptPreview detail={detail} />
                )}
              </ScrollArea>
              {/* Print-only clone — the browser prints just this subtree
                  (see the .printing-receipt thermal rules in index.css).
                  None for a partner group: no priced receipt (ABT-S22 AC9). */}
              {!partner && (
                <div id="receipt-print" aria-hidden="true" className="hidden">
                  <ReceiptPreview detail={detail} />
                </div>
              )}
              <Separator className="my-4" />
              <div className="flex gap-2">
                <Button
                  size="lg"
                  className="h-14 flex-1 gap-2 text-lg"
                  onClick={handlePrintAll}
                  disabled={printDisabled}
                >
                  {printing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : unprintedCount === 0 ? (
                    <RotateCw className="h-5 w-5" />
                  ) : (
                    <Printer className="h-5 w-5" />
                  )}
                  {partner && summary?.claimable
                    ? claimButtonLabel(summary)
                    : unprintedCount === 0
                      ? 'Cetak Ulang Semua Tiket'
                      : printedCount > 0
                        ? `Cetak ${unprintedCount} Tiket Belum Dicetak`
                        : 'Cetak Semua Tiket'}
                  <kbd className="ml-1 rounded border border-primary-foreground/30 px-1.5 text-xs">
                    Ctrl+P
                  </kbd>
                </Button>
                {!partner && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 gap-2 text-base"
                    onClick={printReceipt}
                  >
                    <Receipt className="h-5 w-5" />
                    Cetak Struk
                  </Button>
                )}
              </div>
              {['CANCELLED', 'REFUND'].includes(String(detail.status)) && (
                <p className="mt-2 text-center text-sm text-amber-600">
                  Pesanan {String(detail.status).toLowerCase()} — tiket tidak dapat dicetak.
                </p>
              )}
              {tickets.length > 0 && (!partner || claimed) && (
                <div className="mt-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Cetak per tiket
                  </p>
                  {printedCount > 0 && (
                    <p className="mb-2 text-xs text-ink-400">
                      <RotateCw className="mr-1 inline h-3 w-3" />
                      sudah dicetak — cetak ulang memakai QR baru.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {tickets.map((t) => (
                      <Button
                        key={t.id}
                        variant="outline"
                        size="sm"
                        disabled={printDisabled}
                        onClick={() => handlePrintOne(t)}
                        title={(t.printCounts ?? 0) > 0 ? 'Cetak ulang dengan QR baru' : 'Cetak'}
                      >
                        {(t.printCounts ?? 0) > 0 && <RotateCw className="mr-1 h-3.5 w-3.5" />}
                        {ticketLabel(t)}
                        {t.visitorType ? ` · ${t.visitorType}` : ''}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <ReprintReasonDialog
        open={reprintTarget !== null}
        onOpenChange={(o) => !o && setReprintTarget(null)}
        count={reprintTarget ? (reprintTarget.kind === 'all' ? reprintTarget.count : 1) : 0}
        submitting={printing}
        onConfirm={confirmReprint}
      />
    </div>
  );
}
